'use client'

import { useState } from 'react'

import { Chip, CodeSnippet, Icon, IconComponent } from '@graftcode/design-system'

import { useLibraries } from '@/core/hooks'

import { DOCS_URL, GATEWAY_FLAGS, RUNTIME_GUIDES } from './const'

import styles from './LibrariesOnboarding.module.scss'

/**
 * Empty-state guide shown when the gateway is running (Vision is connected) but reports no
 * modules — usually because the --modules flag was missing or pointed at the wrong path.
 */
export function LibrariesOnboarding() {
  const { gatewayPath, gatewayPort } = useLibraries()
  const [activeRuntimeId, setActiveRuntimeId] = useState(RUNTIME_GUIDES[0]!.id)

  const target = gatewayPath ? (gatewayPort ? `${gatewayPath}:${gatewayPort}` : gatewayPath) : null
  const activeGuide = RUNTIME_GUIDES.find((guide) => guide.id === activeRuntimeId) ?? RUNTIME_GUIDES[0]!

  return (
    <div className={styles.center}>
      <section className={styles.container} aria-labelledby="libraries-onboarding-title">
        <div className={styles.header}>
          <span className={styles.badge}>
            <IconComponent icon={Icon.BoxRegular} size="32" className={styles.badgeIcon} />
          </span>
          <h2 id="libraries-onboarding-title" className={styles.title}>
            No modules hosted yet
          </h2>
          <p className={styles.subtitle}>
            Vision is connected to your gateway, but it isn&apos;t hosting any modules. This usually means the{' '}
            <code>--modules</code> flag was missing or pointed at the wrong file. Pick your language below to see the
            exact command for hosting a module.
          </p>
          {target && (
            <p className={styles.target}>
              Connected gateway <code>{target}</code>
            </p>
          )}
        </div>

        <div className={styles.runtimeTabs} role="tablist" aria-label="Choose your language">
          {RUNTIME_GUIDES.map((guide) => (
            <Chip
              key={guide.id}
              text={guide.label}
              active={guide.id === activeRuntimeId}
              onClick={() => setActiveRuntimeId(guide.id)}
            />
          ))}
        </div>

        <div className={styles.guide}>
          <CodeSnippet
            className={styles.guideCode}
            code={activeGuide.command}
            language="bash"
            copyButtonText="Copy"
            copyButtonVariant="with-text"
          />
          <p className={styles.guideNote}>{activeGuide.note}</p>
        </div>

        <ul className={styles.tips}>
          <li>
            Shorthand: the first path can be positional — <code>gg ./MyLibrary.dll</code> is the same as using{' '}
            <code>--modules</code>.
          </li>
          <li>
            Host several modules at once by separating paths with commas (same runtime only):{' '}
            <code>--modules ./a.dll,./b.dll</code>.
          </li>
          <li>
            If GG can&apos;t detect the runtime, set it explicitly with <code>--runtime</code> and pass the full path.
          </li>
        </ul>

        <div className={styles.flags}>
          <h3 className={styles.flagsTitle}>Gateway flags</h3>
          <dl className={styles.flagList}>
            {GATEWAY_FLAGS.map((flag) => (
              <div key={flag.flag} className={styles.flag}>
                <dt className={styles.flagName}>
                  <code>{flag.flag}</code>
                </dt>
                <dd className={styles.flagDesc}>{flag.description}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className={styles.actions}>
          <a className={styles.docsLink} href={DOCS_URL} target="_blank" rel="noopener noreferrer">
            Read the documentation
          </a>
        </div>
      </section>
    </div>
  )
}
